import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, loginRequest, registerRequest, clearSession } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { ZAR } from "@/lib/cart";
import { GoogleLogo, PencilSimple, Check, X, SignOut, User, Phone, MapPin, EnvelopeSimple, Package, Eye, EyeSlash } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Account() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = () => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/orders/mine/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setProfile(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!user) {
    return (
      <div className="mx-auto max-w-[560px] px-4 py-10 sm:px-6 sm:py-16 lg:px-10">
        <div className="font-label text-xs text-[#FF5A1F]">/ SIGN IN</div>
        <h1 className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-4xl">Welcome back.</h1>
        <p className="mt-3 text-sm text-white/60">Pick your favourite way to sign in.</p>

        <button
          data-testid="auth-google-btn"
          onClick={startGoogleLogin}
          className="mt-8 w-full flex items-center justify-center gap-3 border border-white/15 bg-white text-black py-3.5 font-label text-xs hover:-translate-y-0.5 transition-transform"
        >
          <GoogleLogo size={18} weight="bold" />
          Continue with Google
        </button>

        <div className="my-8 flex items-center gap-3 text-[10px] font-label text-white/30">
          <div className="flex-1 border-t border-white/10" />
          OR
          <div className="flex-1 border-t border-white/10" />
        </div>

        <EmailAuth onDone={refresh} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 sm:py-14 lg:px-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-label text-xs text-[#00FFC2]">/ MY PROFILE</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">
            Hi, <span className="text-[#FF5A1F]">{(profile?.name || user.email || "").split("@")[0]}</span>
          </h1>
        </div>
        <button
          data-testid="account-signout"
          onClick={() => {
            clearSession();
            nav("/");
          }}
          className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 font-label text-xs hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
        >
          <SignOut size={14} weight="bold" /> Sign out
        </button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid gap-3 grid-cols-2 md:grid-cols-4">
        <StatCard
          testId="stat-spent"
          label="TOTAL SPENT"
          value={stats ? ZAR(stats.total_spent) : "R —"}
          color="#00FFC2"
        />
        <StatCard
          testId="stat-orders"
          label="ORDERS"
          value={stats?.total_orders ?? 0}
          color="#FFC900"
        />
        <StatCard
          testId="stat-points"
          label="LOYALTY POINTS"
          value={stats?.points ?? 0}
          hint="100 pts = R10 off"
          color="#FF5A1F"
        />
        <StatCard
          testId="stat-tier"
          label="TIER"
          value={stats?.tier ?? "New"}
          color="#8B5CF6"
        />
      </div>

      {/* Profile editor */}
      <div className="mt-10 border border-white/10 bg-[#0d0d0d]">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <div className="font-label text-[10px] text-white/50">/ PROFILE DETAILS</div>
          {!editing ? (
            <button
              data-testid="profile-edit-btn"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 text-[10px] font-label text-[#00FFC2] hover:text-white transition-colors"
            >
              <PencilSimple size={12} weight="bold" /> Edit
            </button>
          ) : (
            <button
              data-testid="profile-cancel-btn"
              onClick={() => setEditing(false)}
              className="inline-flex items-center gap-2 text-[10px] font-label text-white/50 hover:text-white transition-colors"
            >
              <X size={12} weight="bold" /> Cancel
            </button>
          )}
        </div>
        {profile && (
          <ProfileEditor
            profile={profile}
            editing={editing}
            onSaved={(fresh) => {
              setProfile(fresh);
              setEditing(false);
              toast.success("Profile updated");
              refresh?.();
            }}
          />
        )}
      </div>

      {/* Referral code */}
      {profile?.referral_code && (
        <div className="mt-6 border border-white/10 bg-[#0d0d0d] p-5">
          <div className="font-label text-[10px] text-[#FFC900]">/ YOUR REFERRAL CODE</div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div className="font-display text-2xl font-black tracking-widest text-white">
              {profile.referral_code}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/?ref=${profile.referral_code}`
                );
                toast.success("Referral link copied");
              }}
              className="border border-white/15 px-3 py-2 font-label text-[10px] hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
            >
              COPY LINK
            </button>
          </div>
          <div className="mt-2 text-[11px] text-white/50">
            Share your link — you both get 50 loyalty points on their first order.
          </div>
        </div>
      )}

      {/* Orders */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <Package size={16} weight="duotone" className="text-white/50" />
          <div className="font-label text-[10px] text-white/50">RECENT ORDERS</div>
        </div>
        <div className="border border-white/10">
          {orders.length === 0 ? (
            <div className="p-8 text-center text-sm text-white/40">No orders yet.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block">
                {orders.map((o) => (
                  <div
                    key={o.order_id}
                    className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4 border-b border-white/10 p-4 last:border-b-0 items-center hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="font-mono text-xs">{o.order_id}</div>
                    <div className="text-xs text-white/50">{new Date(o.created_at).toLocaleDateString()}</div>
                    <div className="text-xs font-label uppercase">
                      <span className={`px-2 py-1 border ${statusColor(o.status)}`}>{o.status}</span>
                    </div>
                    <div className="text-right font-display font-bold text-[#00FFC2]">{ZAR(o.amount)}</div>
                  </div>
                ))}
              </div>
              {/* Mobile stacked */}
              <div className="sm:hidden divide-y divide-white/10">
                {orders.map((o) => (
                  <div key={o.order_id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[11px]">{o.order_id}</div>
                      <span className={`px-2 py-0.5 border font-label text-[9px] uppercase ${statusColor(o.status)}`}>
                        {o.status}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-[11px] text-white/50">
                        {new Date(o.created_at).toLocaleDateString()}
                      </div>
                      <div className="font-display text-base font-bold text-[#00FFC2]">{ZAR(o.amount)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function statusColor(s) {
  if (s === "paid" || s === "delivered") return "border-[#00FFC2] text-[#00FFC2]";
  if (s === "shipped") return "border-[#FFC900] text-[#FFC900]";
  if (s === "cancelled" || s === "failed") return "border-red-500 text-red-500";
  return "border-white/20 text-white/60";
}

function StatCard({ label, value, hint, color, testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#0d0d0d] p-4">
      <div className="font-label text-[9px] text-white/40 tracking-wider">{label}</div>
      <div
        className="mt-2 font-display text-2xl font-black tracking-tight sm:text-3xl"
        style={{ color }}
      >
        {value}
      </div>
      {hint && <div className="mt-1 text-[10px] text-white/40">{hint}</div>}
    </div>
  );
}

function ProfileEditor({ profile, editing, onSaved }) {
  const [form, setForm] = useState({
    name: profile.name || "",
    phone: profile.phone || "",
    shipping_address: profile.shipping_address || "",
    marketing_opt_in: !!profile.marketing_opt_in,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      name: profile.name || "",
      phone: profile.phone || "",
      shipping_address: profile.shipping_address || "",
      marketing_opt_in: !!profile.marketing_opt_in,
    });
  }, [profile]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", form);
      onSaved(data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="grid gap-4 p-5 sm:grid-cols-2">
        <ReadRow icon={EnvelopeSimple} label="Email" value={profile.email} />
        <ReadRow icon={User} label="Name" value={profile.name || "—"} />
        <ReadRow icon={Phone} label="Phone" value={profile.phone || "—"} />
        <ReadRow
          icon={MapPin}
          label="Shipping address"
          value={profile.shipping_address || "—"}
        />
        <ReadRow
          icon={Check}
          label="Marketing emails"
          value={profile.marketing_opt_in ? "Subscribed" : "Not subscribed"}
        />
      </div>
    );
  }

  return (
    <form onSubmit={save} className="grid gap-4 p-5 sm:grid-cols-2">
      <Field label="Full name">
        <input
          data-testid="profile-name-input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full border border-white/15 bg-black px-3 py-2.5 text-sm outline-none focus:border-[#FF5A1F]"
        />
      </Field>
      <Field label="Phone">
        <input
          data-testid="profile-phone-input"
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="+27 82 123 4567"
          className="w-full border border-white/15 bg-black px-3 py-2.5 text-sm outline-none focus:border-[#FF5A1F]"
        />
      </Field>
      <Field label="Shipping address" className="sm:col-span-2">
        <textarea
          data-testid="profile-address-input"
          value={form.shipping_address}
          onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
          rows={3}
          placeholder="Street, Suburb, City, Postal Code"
          className="w-full border border-white/15 bg-black px-3 py-2.5 text-sm outline-none focus:border-[#FF5A1F]"
        />
      </Field>
      <label className="sm:col-span-2 flex items-center gap-3 text-xs text-white/70">
        <input
          data-testid="profile-marketing-checkbox"
          type="checkbox"
          checked={form.marketing_opt_in}
          onChange={(e) => setForm({ ...form, marketing_opt_in: e.target.checked })}
          className="h-4 w-4 accent-[#FF5A1F]"
        />
        Send me flavour drops, discounts & member perks.
      </label>
      <button
        data-testid="profile-save-btn"
        disabled={saving}
        className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#FF5A1F] py-2.5 font-label text-xs text-black transition-transform hover:-translate-y-0.5 disabled:opacity-50"
      >
        <Check size={14} weight="bold" />
        {saving ? "Saving..." : "Save changes"}
      </button>
    </form>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <div className="mb-1 font-label text-[9px] text-white/50 tracking-wider">{label}</div>
      {children}
    </label>
  );
}

function ReadRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} weight="duotone" className="text-[#00FFC2] mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="font-label text-[9px] text-white/40">{label}</div>
        <div className="mt-0.5 text-sm text-white/90 break-words">{value}</div>
      </div>
    </div>
  );
}

/* ------- EMAIL sign-in / register ------- */
function EmailAuth({ onDone }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") await loginRequest(email, password);
      else await registerRequest(email, password, name);
      await onDone();
      toast.success(mode === "login" ? "Signed in" : "Welcome to Vape Essence");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || (mode === "login" ? "Login failed" : "Register failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex gap-2 text-[10px] font-label">
        <button
          type="button"
          data-testid="auth-mode-login"
          onClick={() => setMode("login")}
          className={`flex-1 border py-2 ${mode === "login" ? "border-white bg-white text-black" : "border-white/15 text-white/50"}`}
        >
          Sign in
        </button>
        <button
          type="button"
          data-testid="auth-mode-register"
          onClick={() => setMode("register")}
          className={`flex-1 border py-2 ${mode === "register" ? "border-white bg-white text-black" : "border-white/15 text-white/50"}`}
        >
          New account
        </button>
      </div>
      {mode === "register" && (
        <input
          data-testid={AUTH.registerName}
          placeholder="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
        />
      )}
      <input
        data-testid={mode === "login" ? AUTH.loginEmail : AUTH.registerEmail}
        type="email"
        required
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
      />
      <div className="relative">
        <input
          data-testid={mode === "login" ? AUTH.loginPassword : AUTH.registerPassword}
          type={showPassword ? "text" : "password"}
          required
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border border-white/15 bg-black px-4 py-3 pr-11 text-sm outline-none focus:border-[#FF5A1F]"
        />
        <button
          type="button"
          data-testid="auth-toggle-password"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-2 flex items-center justify-center px-2 text-white/60 hover:text-[#FF5A1F]"
        >
          {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {err && (
        <div data-testid={AUTH.errorMsg} className="text-xs text-[#FF5A1F]">
          {err}
        </div>
      )}
      <button
        data-testid={mode === "login" ? AUTH.loginSubmit : AUTH.registerSubmit}
        disabled={loading}
        className="w-full bg-[#FF5A1F] py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
      >
        {loading ? "..." : mode === "login" ? "Sign in with email" : "Create account"}
      </button>
    </form>
  );
}
